import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QuizFilters } from "./types";
import { updateOrgStructure } from "../../../../../store/orgStructure/useCases/updateOrgStructure/action";
import { selectOrgStructure } from "../../../../../store/orgStructure/repository/selector";
import { getQuizList } from "../../../../../store/quiz/useCases/getQuizList/action";
import { selectQuiz } from "../../../../../store/quiz/repository/selector";
import { deleteQuiz } from "../../../../../store/quiz/useCases/deleteQuiz/action";
import { updateQuiz } from "../../../../../store/quiz/useCases/updateQuiz/action";
import toast from "react-hot-toast";
import { tokens } from "../../../../../locales/tokens";
import { uploadQuizReport } from "../../../../../store/quiz/useCases/uploadQuizReport/action";
import { TIMESTAMP_DAY } from "../../../../../config";
import { setQuizList } from "../../../../../store/quiz/repository/slice";

export const useQuizList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const findStatusLabel = (value: string) => {
    if (value === "Available") return t(tokens.documents.publish);
    if (value === "Hidden") return t(tokens.documents.notPublish);
    return "";
  };

  const initialFilters: QuizFilters = {
    page: Number(searchParams.get("page")) || 1,
    rowsPerPage: Number(searchParams.get("rowsPerPage")) || 30,
    search: searchParams.get("search") || "",
    tab:
      (searchParams.get("tab") as unknown as QuizFilters["tab"]) ||
      ("Active" as QuizFilters["tab"]),
    status: searchParams.get("status")
      ? {
          value: searchParams.get("status"),
          label: findStatusLabel(searchParams.get("status") as string),
        }
      : null,
    initiator: searchParams.get("initiator")
      ? {
          value: searchParams.get("initiator"),
          label: searchParams.get("initiatorName"),
        }
      : null,
    date: searchParams.get("date") ? Number(searchParams.get("date")) : null,
  };

  const [filters, updateFilters] = useState<QuizFilters>(initialFilters);
  const [searchText, setSearchText] = useState(filters.search);

  const { orgStructure } = useSelector(selectOrgStructure);
  const {
    quizList,
    quizCount,
    isLoading,
    updateQuizError,
    deleteQuizError,
    getQuizListError,
  } = useSelector(selectQuiz);
  const [pageAmount, setPageAmount] = useState(0);
  const [updateClick, setUpdateClick] = useState(false);
  const [deleteClick, setDeleteClick] = useState(false);
  const [isProccessQuiz, setProcessQuiz] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params: any = {
      page: filters.page,
      rowsPerPage: filters.rowsPerPage,
      tab: filters.tab,
    };
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status.value;
    if (filters.initiator) {
      params.initiator = filters.initiator.value;
      params.initiatorName = filters.initiator.label;
    }
    if (filters.date) params.date = filters.date.toString();

    setSearchParams(params);
  }, [filters, setSearchParams]);

  useEffect(() => {
    dispatch(getQuizList(combineRequestBody()));
    setProcessQuiz(true);
    return () => {
      dispatch(setQuizList([]));
    };
  }, [filters]);

  const combineRequestBody = () => {
    const body: any = { page: filters.page, size: filters.rowsPerPage };
    if (filters.search) body.search = filters.search;
    if (filters.initiator) body.initiatorId = filters.initiator.value;
    if (filters.status) body.status = filters.status.value;
    if (filters.date) {
      const startDateString = new Date(filters.date).toISOString();
      body.startDate = startDateString;
      const endDateString = new Date(
        filters.date + TIMESTAMP_DAY
      ).toISOString();
      body.endDate = endDateString;
    }
    if (filters.tab !== "Active") body.deleted = "true";
    return body;
  };

  useEffect(() => {
    if (quizCount) {
      setPageAmount(Math.ceil(quizCount / filters.rowsPerPage));
    }
  }, [quizCount]);

  useEffect(() => {
    dispatch(updateOrgStructure());
  }, []);

  useEffect(() => {
    if (!isLoading && updateClick) {
      if (updateQuizError) {
        toast.error(updateQuizError);
      } else {
        toast.success(t(tokens.quiz.message.quizUpdated));
        dispatch(getQuizList(combineRequestBody()));
      }
      setUpdateClick(false);
    }
  }, [updateQuizError, isLoading]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (deleteQuizError) {
        toast.error(deleteQuizError);
      } else {
        filters.tab === "Active"
          ? toast.success(t(tokens.quiz.message.quizRemoved))
          : toast.success(t(tokens.quiz.message.quizRemovedHard));
        dispatch(getQuizList(combineRequestBody()));
      }
      setDeleteClick(false);
    }
  }, [deleteQuizError, isLoading]);

  useEffect(() => {
    if (!isLoading && isProccessQuiz) {
      if (getQuizListError) toast.error(getQuizListError);
      setTimeout(() => setProcessQuiz(false), 400);
    }
  }, [getQuizListError, isLoading]);

  const onSearchQuiz = (value: string) => {
    updateFilters((prev) => ({ ...prev, search: value, page: 1 }));
    scrollTopList();
  };
  const { debouncedWrapper: debouncedHandleQuizSearch } = useDebounce(
    onSearchQuiz,
    700
  );

  const handleSearchQuizText = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedHandleQuizSearch(e.target.value);
  };

  const handleTabsChange = (event: ChangeEvent<{}>, tab: string): void => {
    updateFilters((prev) => ({
      ...prev,
      tab: tab as QuizFilters["tab"],
      page: 1,
    }));
    scrollTopList();
  };

  const handlePageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number): void => {
      updateFilters((prev) => ({ ...prev, page }));
      scrollTopList();
    },
    []
  );

  const handleDateChange = useCallback((date: Date | null): void => {
    updateFilters((prev) => ({
      ...prev,
      page: 1,
      date: date?.valueOf() || null,
    }));
    scrollTopList();
  }, []);

  const changeStatus = useCallback((status: any) => {
    updateFilters((prev) => ({ ...prev, page: 1, status }));
    scrollTopList();
  }, []);

  const onSelectInitiator = (selectedKeysValue: string, node: any) => {
    if ("children" in node) return;
    updateFilters((prev) => ({ ...prev, page: 1, initiator: node }));
    scrollTopList();
  };

  const onChangeInitiator = () => {
    if (filters.initiator) {
      updateFilters((prev) => ({ ...prev, page: 1, initiator: null }));
    }
  };

  const resetFilters = useCallback((): void => {
    updateFilters({
      page: 1,
      rowsPerPage: 30,
      search: "",
      tab: "Active",
      status: null,
      initiator: null,
      date: null,
    });
    scrollTopList();
  }, []);

  const handleStatus = (id: number, status: string) => {
    const newStatus = status === "Hidden" ? "Available" : "Hidden";
    dispatch(updateQuiz({ body: { id, status: newStatus } }));
    setUpdateClick(true);
  };

  const handleDelete = (id: number, mode?: string) => {
    dispatch(deleteQuiz({ id, mode }));
    setDeleteClick(true);
  };

  const handleUploadReport = (id: number, name: string) => {
    dispatch(uploadQuizReport({ id, name }));
  };

  const scrollTopList = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return {
    searchText,
    handleSearchQuizText,
    filters,
    handleTabsChange,
    orgStructure,
    changeStatus,
    resetFilters,
    handleDateChange,
    onSelectInitiator,
    onChangeInitiator,
    handlePageChange,
    handleStatus,
    handleDelete,
    handleUploadReport,
    pageAmount,
    quizList,
    isProccessQuiz,
    scrollRef,
  };
};
